/**
 * MYAC-1607 — the client's own categories on a watched reference.
 *
 * Stored on the `regulation_lists` row as `tags: {category: [values]}` plus the
 * constructed `tags_flat: ['category:<value>']` (the same shape as the catalogue
 * requirements' tags; `tags_flat` is matched, never parsed). The key is the
 * platform's; the values are the client's, kept as typed. Pure helpers only: the
 * writes live in the regulatory monitoring store.
 */

import { safeCollator } from './safeCollator'

/** The one facet key the platform owns on a watch row. */
export const CATEGORY_FACET = 'category'

/** Filter value standing for "no category at all" (never a stored value: a
 *  stored one is trimmed text, and this one holds a NUL). */
export const UNCATEGORISED = '\u0000uncategorised'

/** Trim, drop empties, dedupe case-insensitively (first spelling wins). */
export function normalizeCategories(values: readonly string[]): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const raw of values) {
    const value = raw.trim()
    if (!value) continue
    const key = value.toLocaleLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    out.push(value)
  }
  return out
}

/** The two fields a category write sets, always together. No category left
 *  writes the empty map and list, so a filter on `tags_flat` drops the row. */
export function categoryTagsPayload(values: readonly string[]): {
  tags: Record<string, string[]>
  tags_flat: string[]
} {
  const categories = normalizeCategories(values)
  if (categories.length === 0) return { tags: {}, tags_flat: [] }
  return {
    tags: { [CATEGORY_FACET]: categories },
    tags_flat: categories.map((v) => `${CATEGORY_FACET}:${v}`).sort(),
  }
}

/** The row's categories as stored, tolerant of any shape (tolerant reader). */
export function categoriesOf(tags: unknown): string[] {
  if (!tags || typeof tags !== 'object') return []
  const values = (tags as Record<string, unknown>)[CATEGORY_FACET]
  if (!Array.isArray(values)) return []
  return normalizeCategories(values.filter((v): v is string => typeof v === 'string'))
}

/** A bulk edit: add some categories, remove others, keep the rest as they are. */
export function applyCategoryEdit(
  current: readonly string[],
  edit: { add: readonly string[]; remove: readonly string[] },
): string[] {
  const removed = new Set(normalizeCategories(edit.remove).map((v) => v.toLocaleLowerCase()))
  const kept = current.filter((v) => !removed.has(v.toLocaleLowerCase()))
  return normalizeCategories([...kept, ...edit.add])
}

/** Same list, ignoring case and order: an edit that changes nothing writes nothing. */
export function sameCategories(a: readonly string[], b: readonly string[]): boolean {
  const norm = (l: readonly string[]) =>
    normalizeCategories(l)
      .map((v) => v.toLocaleLowerCase())
      .sort()
      .join('\u0000')
  return norm(a) === norm(b)
}

/** Every category used in a set of rows, sorted for the locale (the filter's
 *  options and the editor's suggestions). Case variants count once. */
export function distinctCategories(lists: Iterable<readonly string[]>, locale: string): string[] {
  return normalizeCategories(Array.from(lists).flat()).sort((a, b) => safeCollator(locale).compare(a, b))
}

/** A row matches a category filter if it carries ANY selected value; the
 *  `UNCATEGORISED` value matches the rows with none. An empty filter keeps all. */
export function matchesCategoryFilter(categories: readonly string[], selected: readonly string[]): boolean {
  if (selected.length === 0) return true
  if (categories.length === 0) return selected.includes(UNCATEGORISED)
  const lower = new Set(categories.map((v) => v.toLocaleLowerCase()))
  return selected.some((s) => s !== UNCATEGORISED && lower.has(s.toLocaleLowerCase()))
}

/** One group of a "group by category" display. `category` null = the rows with none. */
export interface CategoryGroup<T> {
  category: string | null
  items: T[]
}

/** Groups items by category, sorted for the locale, the uncategorised last.
 *  An item with two categories is in both groups: each group is complete. */
export function groupByCategory<T>(
  items: readonly T[],
  categoriesFor: (item: T) => readonly string[],
  locale: string,
): CategoryGroup<T>[] {
  const byKey = new Map<string, CategoryGroup<T>>()
  const none: T[] = []
  for (const item of items) {
    const categories = normalizeCategories(categoriesFor(item))
    if (categories.length === 0) {
      none.push(item)
      continue
    }
    for (const category of categories) {
      const key = category.toLocaleLowerCase()
      let group = byKey.get(key)
      if (!group) {
        group = { category, items: [] }
        byKey.set(key, group)
      }
      group.items.push(item)
    }
  }
  const collator = safeCollator(locale)
  const groups = Array.from(byKey.values()).sort((a, b) => collator.compare(a.category ?? '', b.category ?? ''))
  if (none.length > 0) groups.push({ category: null, items: none })
  return groups
}
